import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

export interface CommsAudienceFilter {
  source: "insurance" | "static";
  status?: "active" | "cancelled" | "all";
  category?: string;
  recipients?: { email: string; name?: string }[];
}

export interface CommsAudience {
  id: string;
  name: string;
  description: string | null;
  filterJson: CommsAudienceFilter | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  recordCount: number;
}

export interface CommsCampaign {
  id: string;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  audienceId: string | null;
  audience: { id: string; name: string } | null;
  status: "DRAFT" | "SENDING" | "SENT" | "FAILED";
  sentAt: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommsRecipientPreview {
  email: string;
  name: string | null;
}

export interface CreateAudienceInput {
  name: string;
  description?: string;
  filterJson?: CommsAudienceFilter | null;
}

export interface CreateCampaignInput {
  subject: string;
  bodyHtml: string;
  bodyText: string;
  audienceId?: string | null;
}

export function useAudiences() {
  return useQuery({
    queryKey: ["comms", "audiences"],
    queryFn: () => api.get<{ items: CommsAudience[] }>("/api/comms/audiences"),
  });
}

export function useCreateAudience() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAudienceInput) => api.post<{ audience: CommsAudience }>("/api/comms/audiences", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comms", "audiences"] });
    },
  });
}

export function useUpdateAudience(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAudienceInput) => api.put<{ audience: CommsAudience }>(`/api/comms/audiences/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comms", "audiences"] });
    },
  });
}

export function useDeleteAudience() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/comms/audiences/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comms", "audiences"] });
    },
  });
}

export function useCampaigns() {
  return useQuery({
    queryKey: ["comms", "campaigns"],
    queryFn: () => api.get<{ items: CommsCampaign[] }>("/api/comms/campaigns"),
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCampaignInput) => api.post<{ campaign: CommsCampaign }>("/api/comms/campaigns", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comms", "campaigns"] });
    },
  });
}

export function usePreviewCampaign() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ recipients: CommsRecipientPreview[]; count: number }>(`/api/comms/campaigns/${id}/preview`),
  });
}

export function useSendCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ campaign: CommsCampaign; sent: number; failed: number }>(`/api/comms/campaigns/${id}/send`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comms", "campaigns"] });
    },
  });
}
